import { useState, useEffect } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { eventService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';

const EventForm = ({ event, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    paragraphs: [''],
    tags: ''
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        paragraphs: event.paragraphs || [''],
        tags: event.tags?.join(', ') || ''
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleParagraphChange = (index, value) => {
    const newParagraphs = [...formData.paragraphs];
    newParagraphs[index] = value;
    setFormData(prev => ({ ...prev, paragraphs: newParagraphs }));
  };

  const addParagraph = () => {
    setFormData(prev => ({ ...prev, paragraphs: [...prev.paragraphs, ''] }));
  };

  const removeParagraph = (index) => {
    const newParagraphs = formData.paragraphs.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, paragraphs: newParagraphs }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    
    setThumbnail(file);
    setError('');
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setRemovePdf(false);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setRemovePdf(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('paragraphs', JSON.stringify(formData.paragraphs));
      formDataToSend.append('tags', formData.tags);
      
      if (thumbnail) {
        formDataToSend.append('thumbnail', thumbnail);
      }

      if (pdfFile) {
        formDataToSend.append('pdfFile', pdfFile);
      }
      if (removePdf) {
        formDataToSend.append('removePdf', 'true');
      }

      if (event) {
        await eventService.updateEvent(event._id, formDataToSend);
      } else {
        await eventService.createEvent(formDataToSend);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('API Error:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('adminToken');
        setTimeout(() => window.location.href = '/login', 2000);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err.message || 'An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
          {error}
        </div>
      )}
      
      {event?.thumbnail && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Thumbnail
          </label>
          <img 
            src={getImageSrc(event.thumbnail)} 
            alt="Current thumbnail" 
            className="h-32 w-full object-contain rounded border border-gray-200"
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {event ? 'Change Thumbnail' : 'Thumbnail *'}
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleThumbnailChange}
          className="block w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-medium
            file:bg-[#166534] file:text-white
            hover:file:bg-[#0e3d1f]"
          required={!event}
        />
        <p className="mt-1 text-xs text-gray-500">
          Max file size: 5MB (JPEG, PNG)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm
            focus:border-[#166534] focus:ring-[#166534]"
          required
          placeholder="Enter event title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Content *
        </label>
        {formData.paragraphs.map((para, index) => (
          <div key={index} className="mb-3 flex items-start">
            <textarea
              value={para}
              onChange={(e) => handleParagraphChange(index, e.target.value)}
              className="flex-1 rounded-md border-gray-300 shadow-sm
                focus:border-[#166534] focus:ring-[#166534]"
              rows="3"
              required
              placeholder={`Paragraph ${index + 1}`}
            />
            {formData.paragraphs.length > 1 && (
              <button
                type="button"
                onClick={() => removeParagraph(index)}
                className="ml-2 mt-2 p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
              >
                <FiX className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addParagraph}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm
            leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f]
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          <FiPlus className="mr-1" /> Add Paragraph
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tags * (comma separated)
        </label>
        <input
          type="text"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm
            focus:border-[#166534] focus:ring-[#166534]"
          required
          placeholder="tag1, tag2, tag3"
        />
      </div>

      {/* PDF File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          PDF Attachment (optional)
        </label>
        {event?.pdfFile && !removePdf && (
          <div className="mt-2 flex items-center gap-2">
            <a 
              href={event.pdfFile.startsWith('/') ? `${process.env.REACT_APP_API_BASE_URL || ''}${event.pdfFile}` : event.pdfFile}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#166534] hover:underline text-sm"
            >
              📄 Current PDF attached
            </a>
            <button
              type="button"
              onClick={handleRemovePdf}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={handlePdfChange}
          className="mt-1 block w-full text-sm text-gray-500
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-medium
            file:bg-[#166534] file:text-white
            hover:file:bg-[#0e3d1f]"
        />
        {pdfFile && (
          <p className="mt-1 text-sm text-gray-500">
            New PDF selected: {pdfFile.name}
          </p>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center px-4 py-2 border border-gray-300
            shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2
            focus:ring-[#166534]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent
            text-sm font-medium rounded-md shadow-sm text-white bg-[#166534]
            hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2
            focus:ring-[#166534] disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {event ? 'Updating...' : 'Creating...'}
            </>
          ) : event ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
};

export default EventForm;